import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import Draft from 'draft-js';
import { contentSourceHasText } from '@modules/projects/projectDetail/lifeCycle/components/helper';

const NEW_SECTION_TEXT = 'Section';

const _isEmptyNode = (node) => {
  try {
    // const richBlocks = node.richContent.contentSource.blocks || [];
    const emptyTitle = !node.title || node.title?.length < 1;
    const richTextContentValid = contentSourceHasText(
      node.richContent.contentSource,
    );
    return emptyTitle && !richTextContentValid;
  } catch (error) {
    return true;
  }
};

const getTitlePlainText = (title) => {
  if (!['string', 'object'].includes(typeof title)) {
    return '';
  }
  let parsedObject = title;
  if (typeof title === 'string') {
    try {
      parsedObject = JSON.parse(title);
    } catch (e) {
      // console.log(e);
      return title;
    }
  }
  if (parsedObject.hasOwnProperty('blocks')) {
    const draftState = Draft.convertFromRaw(parsedObject);
    return draftState.getPlainText();
  }
  return '';
};

const getNewSectionNumber = (phases = []) => {
  const allNumbers = [...phases]
    .map((e = {}) =>
      Number(getTitlePlainText(e?.title).replace(NEW_SECTION_TEXT, '')),
    )
    .filter((item) => item > 0)
    .sort((a, b) => a - b);

  if (allNumbers[0] !== 1) {
    return 1;
  }

  let i = 0;
  while (i < allNumbers.length - 1) {
    const e = allNumbers[i];
    const next = allNumbers[i + 1];
    if (next - e > 1) {
      break;
    }
    i++;
  }
  return allNumbers[i] + 1;
};

const utils = {
  buildUpdatedModuleForm: (
    originData,
    { location, payload },
    isAdd = false,
  ) => {
    if (location?.length > 0 && payload) {
      const newData = produce(originData, (draft) => {
        if (location.length === 1 || isNaN(location[1])) {
          // you are updating the phase's title/name/date
          const [index] = location;
          const oldData = draft[index];
          draft[index] = { ...oldData, ...payload };
        } else {
          // you are updating the item's title/content
          const [fatherIndex, itemIndex] = location; // phaseIndex, nodeIndex
          let oldData = draft[fatherIndex].lifecycleItems[itemIndex];
          //   || {
          //   title: null,
          //   id: uuidv4(),
          //   key: `${Date.parse(new Date())}`,
          // };
          if (isAdd && !oldData) {
            oldData = {
              title: null,
              id: uuidv4(),
              key: `${Date.parse(new Date())}`,
            };
          }
          if (payload === 'delete') {
            draft[fatherIndex].lifecycleItems = draft[
              fatherIndex
            ].lifecycleItems.filter((e, i) => i !== itemIndex);
          } else if (oldData) {
            draft[fatherIndex].lifecycleItems[itemIndex] = {
              ...oldData,
              ...payload,
            };
          }
        }
      });
      return { touched: true, data: newData };
    }
    // return false;
  },
  removeObjectProperty: (originObject, keyToRemove) =>
    Object.keys(originObject)
      .filter((key) => key !== keyToRemove)
      .reduce(
        (cur, key) => Object.assign(cur, { [key]: originObject[key] }),
        {},
      ),
  getNodesByPhaseId: (moduleRef, id) => {
    const result = moduleRef.current.data.find((item) => item.id === id);
    return result?.lifecycleItems || [];
  },
  getNodes: (moduleFormRef, moduleOrder, containerId) => {
    const sequence = moduleOrder[containerId],
      nodes = utils.getNodesByPhaseId(moduleFormRef, containerId);

    if (!sequence?.length && sequence?.length !== 0) {
      return nodes;
    }
    return sequence.map((index) => nodes[index] || {});
  },
  createNewPhaseWithTemplate: (num, id, type, phaseContents) => ({
    createdAt: new Date(),
    id,
    lifecycleItems: [],
    name: ' new name',
    order: num,
    projectId: '',
    startAt: null,
    title: `Section ${getNewSectionNumber(phaseContents)}`,
    type,
  }),
  buildPhaseDateDetails: (
    phase,
    i,
    phaseDates,
    setPhaseDates,
    type,
    sectionDate,
    defaultName,
  ) => {
    function findNearestPrevDate(arr, index, result) {
      if (result !== null || index === 0) {
        return result;
      }
      index--;
      return findNearestPrevDate(arr, index, arr[index]);
    }

    function findNearestNextDate(arr, index, result) {
      if (result !== null || index === arr.length - 1) {
        return result;
      }
      index++;
      return findNearestNextDate(arr, index, arr[index]);
    }

    let dates;
    if (type === 'Divestiture') {
      dates = [sectionDate ? moment(sectionDate) : null, ...phaseDates];
    } else {
      dates = [...phaseDates, sectionDate ? moment(sectionDate) : null];
    }
    // for divestiture, first should be the index===1 not 0
    const startIndex = type === 'Divestiture' ? i + 1 : i;

    return {
      name: phase.name,
      defaultName,
      dateRange: [
        findNearestPrevDate(dates, startIndex, null)?.endOf('day'),
        findNearestNextDate(dates, startIndex, null)?.startOf('day'),
      ],
      allPhaseDates: phaseDates,
      startAt: phaseDates[i],
      setPhaseDates,
    };
  },
  buildPostBody: (dataCollection, sequences, instanceId) => {
    const EMPTY_NODE_ERROR = 'EMPTY_NODE_ERROR';
    if (!Array.isArray(dataCollection)) {
      return [];
    }
    return dataCollection.map((data) => {
      const currentQueue = sequences?.[data?.id] || [];
      if (!data?.title) {
        throw new Error(EMPTY_NODE_ERROR);
      }
      if (data?.startAt instanceof Date) {
        data.startAt = moment(data.startAt).format();
      }
      const items = Array.isArray(data.lifecycleItems)
        ? data.lifecycleItems
        : [];

      const lifecycleItems = currentQueue.map((index) => {
        const currentNode = items[index] || {
          id: null,
          title: null,
        };

        if (_isEmptyNode(currentNode)) {
          throw new Error(EMPTY_NODE_ERROR);
        }
        if (instanceId) {
          currentNode.instanceId = instanceId;
        }

        return currentNode;
      });

      return {
        ...data,
        lifecycleItems,
      };
    });
  },
};

export default utils;
